/**
 * Env-mapping helpers for Single and Collection entities.
 *
 * Single entities use `envMapping` to override individual fields at runtime.
 * Collection entities use `envDefaults` to auto-seed a default record and lock its env-mapped fields.
 *
 * Fields with a defined env var are returned with `_envLocked: string[]` in the response,
 * and stripped from update payloads silently.
 */

import type { EnvFieldMapping, EnvMapping, EnvDefaults } from '@gzl10/nexus-sdk'

// ── Parsing ────────────────────────────────────────────────────────────────────

/**
 * Parses a raw env var string into the target type.
 */
export function parseEnvValue(value: string, parse?: EnvFieldMapping['parse']): unknown {
  if (parse === 'number') return Number(value)
  if (parse === 'boolean') return value === 'true' || value === '1'
  if (parse === 'json') {
    try { return JSON.parse(value) } catch { return value }
  }
  return value
}

// ── Single entity (envMapping) ─────────────────────────────────────────────────

/**
 * Returns the list of field names whose env var is currently defined.
 */
export function getEnvLockedFields(mapping: EnvMapping): string[] {
  return Object.entries(mapping)
    .filter(([, cfg]) => process.env[cfg.env] !== undefined)
    .map(([field]) => field)
}

/**
 * Overrides DB data with values from env vars where defined.
 * Returns a new object — does not mutate the input.
 */
export function resolveEnvMapping<T extends Record<string, unknown>>(dbData: T, mapping: EnvMapping): T {
  const overrides: Record<string, unknown> = {}
  for (const [field, cfg] of Object.entries(mapping)) {
    const raw = process.env[cfg.env]
    if (raw !== undefined) {
      overrides[field] = parseEnvValue(raw, cfg.parse)
    }
  }
  if (Object.keys(overrides).length === 0) return dbData
  return { ...dbData, ...overrides }
}

/**
 * Removes env-locked fields from an update payload (silent strip).
 * Returns a new object — does not mutate the input.
 */
export function stripEnvLockedFields<T extends Record<string, unknown>>(data: T, mapping: EnvMapping): T {
  const locked = getEnvLockedFields(mapping)
  if (locked.length === 0) return data
  const result = { ...data }
  for (const field of locked) {
    delete result[field]
  }
  return result
}

// ── Collection entity (envDefaults) ───────────────────────────────────────────

/**
 * Returns the default record values from env vars for a collection's envDefaults.
 * Returns null if none of the env vars are defined.
 */
export function resolveEnvDefaults(envDefaults: EnvDefaults): Record<string, unknown> | null {
  const result: Record<string, unknown> = {}
  let hasAny = false
  for (const [field, cfg] of Object.entries(envDefaults.fields)) {
    const raw = process.env[cfg.env]
    if (raw !== undefined) {
      result[field] = parseEnvValue(raw, cfg.parse)
      hasAny = true
    }
  }
  return hasAny ? { ...envDefaults.match, ...result } : null
}

/**
 * Returns the list of field names in envDefaults whose env var is currently defined.
 */
export function getEnvDefaultsLockedFields(envDefaults: EnvDefaults): string[] {
  return Object.entries(envDefaults.fields)
    .filter(([, cfg]) => process.env[cfg.env] !== undefined)
    .map(([field]) => field)
}

/**
 * Checks if a given record matches the envDefaults.match criteria.
 */
export function isEnvDefaultsRecord(record: Record<string, unknown>, envDefaults: EnvDefaults): boolean {
  return Object.entries(envDefaults.match).every(([key, val]) => record[key] === val)
}
